import { createApp } from 'vue'
import { createPinia } from 'pinia'
import type { App } from 'vue'
import 'vue-final-modal/style.css'
import { vfm } from './plugins/vue-final-modal';
import { withBrandIdentity, applyBrandIdentity, isBrandIdentityWidget } from '@widget-utils'
import 'vue3-easy-data-table/dist/style.css';
import { date } from './composables/date.filter'
import VueKonva from 'vue-konva'
import { vInView } from './directives/inView.directive'
import { handlePentaError } from './utils/errorHandler'
import { itemRankingDirective } from './directives/itemRanking.directive'
import { tooltipDirective } from './directives/tooltip.directive'
//import App from './App.vue'
//
const pinia = createPinia()

// Red de seguridad para rechazos de promesas nativas que nadie capturó y que app.config.errorHandler no ve, porque corren
// fuera del ciclo de Vue (setTimeout, addEventListener, sockets, promesas sin await dentro de un handler). Los @click, hooks
// y watchers async ya los cubre app.config.errorHandler. Es global a la página: también captura los rechazos del código
// AngularJS que no sigue la convención try/catch + $applyAsync (Angular solo reenvía a $exceptionHandler los de $q)
window.addEventListener('unhandledrejection', (event) => {
  handlePentaError(event.reason)
  event.preventDefault()
})

type WidgetGetterFunction<T> = (...args: any[]) => Promise<T>
type WidgetEventFunction = (data: any) => void | Promise<void>
type WidgetEvents = {
  suscribe: (ev: string, fn: WidgetEventFunction) => void
  unsuscribe: (ev: string, fn: WidgetEventFunction) => void
}

declare global {
  interface Window {
    vue: {
      openView(view: string, selector: string | Element, rootScope: any, resourceHelperMethods: resourceHelper, factoryBus: any): Promise<App<Element>>,
      openVueView(view: string, selector: string): Promise<App<Element>>,
      preloadView(view: string): Promise<any>,
      openVueComponent(component: string, selector: string): Promise<App<Element>>,
      openVueModals(modal: string, selector: string, props?: any, folder?: string): Promise<App<Element>>,
      openChatLog(chats: any[]): Promise<void>,
      openWidget<Data, Config>(widget: string, selector: string, getConfig: WidgetGetterFunction<Config>, getData: WidgetGetterFunction<Data>, events: WidgetEvents): Promise<App>,
      openItemRankingModal(item: string, itemDefault: boolean): Promise<any>,
      applyBrandIdentity: typeof applyBrandIdentity,
      isBrandIdentityWidget: typeof isBrandIdentityWidget,
      openRmPanel(terminal: any, options?: { viewerOnly?: boolean, simulator?: boolean, tab?: 'console' }): Promise<void>,
      openRmSimulator(): Promise<void>,
      app?: App,
    }
    appNavigator: { pushPage: (path: string, options?: any) => void }
    i18next: { t: Function }
    terminalUtils: {
      openTerminals(mode: string, items: string[], color: string | null, title: string, options?: { showBack?: boolean, onBack?: () => void }): void
    }
    profile: any,
    rankingUtils: { matchItemRanking(profile: any, itemRankings: string[]): boolean }
    sharedCfg: any
    enterprise: string | null
    player: any
    path: any
    hostAdapter: any
    ons: { notification: { toast: (message: string, options?: any) => void } }
  }
}

// Configuración común a toda app Vue montada dentro de mobile (Angular): traducción, filtros, plugins, directivas y manejo de errores
function configureApp(app: App): App {
  app.config.errorHandler = (err) => handlePentaError(err)
  app.config.globalProperties.$t = function () { return window.i18next.t.apply(this, arguments) }
  app.config.globalProperties.$filters = { date }
  app.directive('item-ranking', itemRankingDirective)
  app.directive('tooltip', tooltipDirective)
  app.directive('in-view', vInView)
  return app.use(vfm).use(VueKonva).use(pinia)
}

type resourceHelper = {
  uploadTask: () => Promise<any>
  humanFileSize: () => string,
  getFileUrl: (object: any, filename: string, crc: string, apiUrl: string, updatedFiles: any, sharedCfg: any) => string,
  calculateCRC: (file: File, rootScope: any, result?: object) => Promise<string>
}

// Declarar acá los tipos de los filtros creados
interface filters {
  date: typeof date
}

// Acá se exitenden los tipos que espera Vue en el contenido de los componentes. Esto se hace para evitar errores al usar filtros en las templates
declare module "@vue/runtime-core" {
  export interface ComponentCustomProperties {
    $filters: filters,
    $t: (string: string, options?: object) => string
  }
}

window.vue = {
  openView,
  openVueView,
  preloadView,
  openVueComponent,
  openVueModals,
  openChatLog,
  openWidget,
  openItemRankingModal,
  // Identidad de la empresa en widgets: el modal de estilo (AngularJS) bloquea los campos que pisa applyBrandIdentity
  // y tvPlayerWidget.factory usa isBrandIdentityWidget para saber qué widgets son compatibles
  applyBrandIdentity,
  isBrandIdentityWidget,
  openRmPanel,
  openRmSimulator,
}

// Módulos de vistas ya importados (por nombre de vista), para montarlas sin esperar un import asíncrono
const viewModules = new Map<string, any>()

// Importa el módulo de una vista y lo guarda en viewModules. openSection (AngularJS) la llama antes de cambiar de
// página, así openView monta la vista apenas se inserta la página en vez de mostrarla vacía mientras se descarga.
// in: view (nombre del archivo en views/, sin .vue)
// out: el módulo de la vista
async function preloadView(view: string): Promise<any> {
  if (!viewModules.has(view)) viewModules.set(view, await import(`./views/${view}.vue`))
  return viewModules.get(view)
}

//
async function openView(view: string, selector: string | Element, rootScope: any, resourceHelperMethods: resourceHelper, factoryBus: any): Promise<App<Element>> {
  // sin await si ya está precargada: el montaje queda antes del primer pintado de la página
  let module = viewModules.get(view) ?? await preloadView(view);
  let app = createApp(module.default)
  window.vue.app = app;
  // Se agrega traducción por i18next
  app.provide('ngRootScope', rootScope) // Hago disponible el rootScope de Angular por si es necesario. Doc de Provide/Inject -> https://vuejs.org/guide/components/provide-inject.html
  app.provide('factoryBus', factoryBus)

  app.config.globalProperties.resourceHelperMethods = resourceHelperMethods
  app.config.globalProperties.ngRootScope = rootScope
  configureApp(app).mount(selector)
  return app;
}

async function openVueView(view: string, selector: string): Promise<App<Element>> {
  let module = viewModules.get(view) ?? await preloadView(view);
  let app = createApp(module.default)
  window.vue.app = app;

  configureApp(app).mount(selector)
  return app;
}

async function openVueComponent(component: string, selector: string, props?: any): Promise<App<Element>> {
  let module = await import(`./components/${component}.vue`);
  let app = createApp(module.default, props || {})
  window.vue.app = app;

  configureApp(app).mount(selector)
  return app;
}

async function openVueModals(modal: string, selector: string, props?: any, folder?: string): Promise<App<Element>> {
  let module = folder
    ? await import(`./modals/${folder}/${modal}.vue`)
    : await import(`./modals/${modal}.vue`);
  let app = createApp(module.default, props || {})
  window.vue.app = app;

  configureApp(app).mount(selector)
  return app;
}

// Abre el historial de chat (ChatLogModal.vue) desde AngularJS con una raíz efímera que se desmonta al cerrarlo
// in: chats [ChatLog] (tal como los devuelve chat.resource)
// out: Promise que resuelve al cerrar el historial
async function openChatLog(chats: any[]): Promise<void> {
  const { default: ChatLogModalMount } = await import('./components/ChatLogModalMount.vue');
  const container = document.createElement('div');
  document.body.appendChild(container);
  let app: App<Element> | undefined;
  try {
    await new Promise<void>((resolve, reject) => {
      // Monta sin openVueComponent para no reemplazar window.vue.app
      app = configureApp(createApp(ChatLogModalMount, { chats, onResolve: resolve, onReject: reject }));
      app.mount(container);
    });
  } finally {
    app?.unmount();
    container.remove();
  }
}

// Abre el modal de edición de jerarquización de un item (EditItemRankingModal.vue) desde AngularJS
// puro, sin que haya ninguna vista Vue ya montada en la página: monta un componente raíz efímero
// (ItemRankingModalMount, que solo aporta el <ModalsContainer /> que vue-final-modal necesita) en un
// div descartable, y lo desmonta al resolver/rechazar. Reemplaza al viejo modal Angular
// 'itemRanking.modal', invocado desde itemRanking.directive.js (ctrl/cmd+click) y desde
// newRoleConfig.subview.controller.js.
async function openItemRankingModal(item: string, itemDefault: boolean): Promise<any> {
  const container = document.createElement('div');
  container.id = `item-ranking-modal-mount-${Math.random().toString(36).slice(2)}`;
  document.body.appendChild(container);
  let app: App<Element> | undefined;
  try {
    return await new Promise((resolve, reject) => {
      openVueComponent('ItemRankingModalMount', `#${container.id}`, { item, itemDefault, onResolve: resolve, onReject: reject })
        .then(mountedApp => { app = mountedApp; })
        .catch(reject);
    });
  } finally {
    app?.unmount();
    container.remove();
  }
}

// Panel RM abierto (hay uno solo a la vez, ver openRmPanel)
let rmPanel: { app: App<Element>, container: HTMLElement } | undefined

// Abre el panel RM (gestión remota) de una terminal en una ventana flotante, montado en un div propio del body: lo
// abren igual la vista Vue de Monitoreo y la legacy, y sobrevive a salir de la vista. Comparte `pinia`, así lee el
// store de monitoreo. Hay una sola ventana a la vez: abrir otra cierra la sesión anterior (STOP OPERATOR al desmontar).
// in: terminal ({ _id, name, enterprise, connection }); options.viewerOnly (hardware VIEWER, sin control);
//     options.simulator (player simulado en el navegador, ver remoteManagement/simulator); options.tab ('console' al
//     abrir desde "Consola remota": arranca en la consola con el video en pausa)
// efecto: monta la app del panel y desmonta la anterior
async function openRmPanel(terminal: any, options: { viewerOnly?: boolean, simulator?: boolean, tab?: 'console' } = {}): Promise<void> {
  closeRmPanel()
  const module = await import('./components/remoteManagement/RmPanelMount.vue')
  const container = document.createElement('div')
  document.body.appendChild(container)
  const app = createApp(module.default, { terminal, ...options, onClose: closeRmPanel })
  configureApp(app).mount(container)
  rmPanel = { app, container }
}

function closeRmPanel() {
  rmPanel?.app.unmount()
  rmPanel?.container.remove()
  rmPanel = undefined
}

// Abre el panel RM contra el player simulado, sin backend ni equipo: banco de pruebas y test e2e del panel
async function openRmSimulator(): Promise<void> {
  return openRmPanel({
    _id: 'rm-simulator', name: window.i18next.t('Simulador RM'), enterprise: window.profile?.enterprise,
    connection: { rm: 1, version: 'simulador', wd: 'simulador' },
  }, { simulator: true })
}

/**
 * @param widget Nombre del widget en PascalCase
 * @param selector Selector css del elemento sobre el que se quiere montar el widget
 * @param getConfig Una función que ejecuta el widget para obtener la configuración
 * @param getData Una función que ejecuta el widget para obtener los datos
 * @param events Eventos y sus reacciones
 * @param interactions Interacciones del widget que se ejecutan en angular
 * @param widgetOrigin String que determina el origen del widget
 */

async function openWidget<Data, Config>(widget: string, selector: string, getConfig: WidgetGetterFunction<Config>, getData: WidgetGetterFunction<Data>, widgetEvents?: WidgetEvents, interactions?: any, fontSize?: string, widgetOrigin?: string, id?: number): Promise<App> {
  const widgetComponent = await import(`@widgets/${widget.toLowerCase()}/${widget}.vue`)
  const app = createApp(widgetComponent.default, { getConfig: withBrandIdentity(widget, getConfig), getData, widgetEvents, interactions, widgetOrigin, widgetId: id })
  // app.provide('widgetTools', {getConfig, getData, EventBus})
  app.use(pinia)
  app.mount(selector)
  return app
}
